import { AccountRole } from '../types/auth';
import { api } from './client';

/**
 * The full "yearly subscription + GST, less the current discount, equals amount due"
 * breakdown for each role, for the signed-in person specifically — mirrors exactly
 * what the server would actually charge them right now.
 */
export interface Pricing {
  /** The sticker (pre-discount, pre-GST) yearly price for each role, in cents. */
  baseRoleActivationFeeCents: Record<AccountRole, number>;
  /** Uniform discount, 0-100, applied to every role's base price above. */
  discountPercent: number;
  /** GST rate, 0-100, applied on top of the discounted price. */
  gstPercent: number;
  /**
   * What selecting each role would actually charge right now, in cents. Not always
   * the flat sticker price: for a mid-term upgrade, only the prorated difference
   * from the role currently held — see `prorated`.
   */
  amountDueCents: Record<AccountRole, number>;
  /** True for a role whose `amountDueCents` is a prorated mid-term upgrade cost. */
  prorated: Record<AccountRole, boolean>;
}

export const fetchPricing = async (): Promise<Pricing> => api.get<Pricing>('/pricing');

/** "Free" or "$29.00" — for a single-line price where zero reads better as "Free". */
export const formatPrice = (cents: number): string =>
  cents <= 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;

/** Always a dollar amount, even "$0.00" — for the itemized subscription/GST/total breakdown. */
export const formatCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;
